import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getStringFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export enum DoorDirection {
  East = 'east',
  South = 'south',
  West = 'west',
  North = 'north',
}

export interface GeckoShipDeckDoorJSON {
  type: GeckoFeatureModelType.ShipDeckDoor
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
  direction: DoorDirection
}
export const NewGeckoShipDeckDoorJSON: GeckoShipDeckDoorJSON = {
  type: GeckoFeatureModelType.ShipDeckDoor,
  name: 'New Ship Deck Door',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ],
  },
  direction: DoorDirection.East,
}
export class GeckoShipDeckDoor extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckDoor
  readonly coordinates: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]
  readonly direction: DoorDirection

  /**
   * @param id
   * @param name
   * @param coordinates
   * @param direction
   */
  private constructor(
    id: string,
    name: string,
    coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[],
    direction: DoorDirection
  ) {
    super(id)
    this.name = name
    this.coordinates = coordinates
    this.direction = direction
  }

  public static CreateFromJSON(
    componentID: string,
    doorID: string,
    doorJSON: GeckoShipDeckDoorJSON
  ): GeckoShipDeckDoor {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const doorCoordinates = doorJSON.feature_geometry.coordinates.map(
      (coord) => ({
        x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
        y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
      })
    )

    const doorName = doorJSON.name as string

    const doorDirection = doorJSON.direction

    return new GeckoShipDeckDoor(
      doorID,
      doorName,
      doorCoordinates,
      doorDirection
    )
  }
  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckDoorJSON>
  ): Map<string, GeckoShipDeckDoor> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckDoor.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckDoor>())
  }

  asJSON() {
    return generateDoor(this.name, this.coordinates, this.direction)
  }
}

export function generateDoor(
  name: string,
  coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[],
  direction: DoorDirection
): GeckoShipDeckDoorJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.ShipDeckDoor,
    feature_geometry: {
      coordinates: coordinates.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
    },
    direction: direction,
  }
}

export const ShipDeckDoorFormFields = (doorJSON: GeckoShipDeckDoorJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Watertight Door Name',
      value: doorJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: doorJSON.feature_geometry.coordinates
        .map((coord) => `[${coord.x}, ${coord.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(4),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
    {
      dataKey: 'direction',
      label: 'Door Direction',
      value: doorJSON.direction,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: Object.keys(DoorDirection).map((key) => ({
        key: key,
        value: DoorDirection[key as keyof typeof DoorDirection],
        label: DoorDirection[key as keyof typeof DoorDirection].replace(
          /\b\w/g,
          (c) => c.toUpperCase()
        ),
      })),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const ShipDeckDoorJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getStringFieldByDataKey(formFields, 'coordinates')

  const isValidDoorDirection = (
    value: DoorDirection
  ): value is DoorDirection => {
    return ['east', 'south', 'west', 'north'].includes(value)
  }

  const directionValue = getFormFieldByDataKey(formFields, 'direction')
    ?.value as DoorDirection

  if (!isValidDoorDirection(directionValue)) {
    throw new Error(
      `Invalid door direction '${directionValue}'. Must be one of: east, south, west, north`
    )
  }

  const direction = directionValue

  return generateDoor(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    })),
    direction
  )
}

export const ShipDeckDoorFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckDoor,
  title: 'Watertight Doors',
  singularName: 'Watertight Door',
  fields: ['name', 'coordinates', 'direction'],
  tableMessage:
    'To see how to enter coordinates to render a watertight door correctly, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a> on polygonal plates.',
}
